import React, { useCallback, useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import WorkersMode from "../components/JnJWorkersMode";
import {
  QrCode, RefreshCw, ChevronRight, ChevronDown, Clock4, Sun, Moon, Coffee,
  UtensilsCrossed, LogOut as LogOutIcon, LogIn, Trash2, Plus, MonitorSmartphone,
  Users, ClipboardEdit, Lock, KeyRound, Settings2, Maximize2, X, ShieldCheck,
  FolderOpen, FileText, FileWarning, Megaphone, FilePlus2, Eye, Upload, Share2,
} from "lucide-react";

// ----------------------------------------------------------------------------
// CONSTANTS
// ----------------------------------------------------------------------------
const SUPERVISOR_TOKEN_KEY = "jnj_sup_token";

const EVENT_META = {
  in:        { label: "Clock in",         icon: LogIn,             cls: "text-emerald-300" },
  lunch_out: { label: "Lunch out",        icon: UtensilsCrossed,   cls: "text-amber-300" },
  lunch_in:  { label: "Back from lunch",  icon: UtensilsCrossed,   cls: "text-emerald-300" },
  break_out: { label: "Break out",        icon: Coffee,            cls: "text-sky-300" },
  break_in:  { label: "Back from break",  icon: Coffee,            cls: "text-emerald-300" },
  out:       { label: "Clock out",        icon: LogOutIcon,        cls: "text-rose-300" },
};
const KIND_ORDER = ["in", "lunch_out", "lunch_in", "break_out", "break_in", "out"];

const STATUS_OPTIONS = [
  { key: "Present", short: "P",   cls: "bg-emerald-500 text-emerald-950 hover:bg-emerald-400" },
  { key: "AL",      short: "AL",  cls: "bg-cyan-500 text-cyan-950 hover:bg-cyan-400" },
  { key: "Late",    short: "L",   cls: "bg-amber-500 text-amber-950 hover:bg-amber-400" },
  { key: "Absent",  short: "A",   cls: "bg-red-500 text-red-50 hover:bg-red-400" },
  { key: "Medical", short: "Md",  cls: "bg-sky-500 text-sky-950 hover:bg-sky-400" },
  { key: "MC",      short: "MC",  cls: "bg-violet-500 text-violet-50 hover:bg-violet-400" },
  { key: "MIA",     short: "MIA", cls: "bg-rose-500 text-rose-50 hover:bg-rose-400" },
  { key: "Off",     short: "Off", cls: "bg-slate-500 text-slate-50 hover:bg-slate-400" },
  { key: "NG",      short: "NG",  cls: "bg-indigo-500 text-indigo-50 hover:bg-indigo-400" },
  { key: "NG IN",   short: "Ni",  cls: "bg-teal-500 text-teal-950 hover:bg-teal-400" },
  // Terminal statuses — kept visually distinct and at the end of the row
  { key: "Resign",     short: "Resign",  cls: "bg-orange-700 text-orange-50 hover:bg-orange-600" },
  { key: "Terminated", short: "Term",    cls: "bg-red-800 text-red-50 hover:bg-red-700" },
];

const STATUS_PILL = {
  Present:    "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  AL:         "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  Late:       "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Absent:     "bg-red-500/15 text-red-300 border-red-500/30",
  Medical:    "bg-sky-500/15 text-sky-300 border-sky-500/30",
  MC:         "bg-violet-500/15 text-violet-300 border-violet-500/30",
  MIA:        "bg-rose-500/15 text-rose-300 border-rose-500/30",
  Off:        "bg-slate-500/15 text-slate-300 border-slate-500/30",
  NG:         "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  "NG IN":    "bg-teal-500/15 text-teal-300 border-teal-500/30",
  Resign:     "bg-orange-600/20 text-orange-200 border-orange-500/40",
  Terminated: "bg-red-700/25 text-red-200 border-red-500/40",
};

// Mirrors backend ROSTER_STATUSES_NOTE_REQUIRED — kept here as a fallback
// when the server response doesn't include `statuses_note_required`.
const NOTE_REQUIRED_STATUSES = new Set([
  "Late", "Absent", "Medical", "MC", "MIA", "Off", "NG IN", "Resign", "Terminated",
]);
// Statuses where an attachment is mandatory (resignation letter / termination notice).
const ATTACHMENT_REQUIRED_STATUSES = new Set(["Resign", "Terminated"]);

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return iso; }
}

// ============================================================================
// MAIN
// ============================================================================
export default function JnJ() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [mode, setMode] = useState("qr");  // "qr" | "manual"

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-red-600/20 border border-red-600/40 flex items-center justify-center">
            <QrCode className="w-5 h-5 text-red-300" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">J&amp;J Attendance</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Kenvue &amp; Ceva attendance. Workers self clock-in via QR, on-site supervisors mark manually with their own login.
            </p>
          </div>
        </div>

        {/* Top-level mode tabs */}
        <div className="border-b hairline mb-5 flex items-center gap-1 overflow-x-auto">
          <button
            onClick={() => setMode("qr")}
            data-testid="jnj-mode-qr"
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition flex items-center gap-2 whitespace-nowrap
              ${mode === "qr" ? "border-red-500 text-red-200" : "border-transparent text-slate-400 hover:text-slate-200"}`}
          >
            <QrCode className="w-4 h-4" />
            QR Clock-in
            <span className="text-[10px] text-slate-500 num">(workers)</span>
          </button>
          <button
            onClick={() => setMode("manual")}
            data-testid="jnj-mode-manual"
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition flex items-center gap-2 whitespace-nowrap
              ${mode === "manual" ? "border-amber-500 text-amber-200" : "border-transparent text-slate-400 hover:text-slate-200"}`}
          >
            <ClipboardEdit className="w-4 h-4" />
            Supervisor Manual
            <span className="text-[10px] text-slate-500 num">(on-site)</span>
          </button>
          <button
            onClick={() => setMode("workers")}
            data-testid="jnj-mode-workers"
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition flex items-center gap-2 whitespace-nowrap
              ${mode === "workers" ? "border-sky-500 text-sky-200" : "border-transparent text-slate-400 hover:text-slate-200"}`}
          >
            <FolderOpen className="w-4 h-4" />
            Workers
            <span className="text-[10px] text-slate-500 num">(HR docs)</span>
          </button>
        </div>

        {mode === "qr" && <QrMode />}
        {mode === "manual" && <ManualMode isAdmin={isAdmin} />}
        {mode === "workers" && <WorkersMode isAdmin={isAdmin} />}
      </div>
    </Layout>
  );
}

// ============================================================================
// MODE 1 — QR CLOCK-IN
// ============================================================================
function QrMode() {
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [selectedShift, setSelectedShift] = useState(null);
  const [subTab, setSubTab] = useState("qr"); // qr | log

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/jnj/companies");
      setGroups(data || []);
      setExpanded((cur) => {
        if (Object.keys(cur).length) return cur;
        const init = {};
        (data || []).forEach((g) => { init[g.parent] = true; });
        return init;
      });
      if (!selectedShift && data?.length && data[0].shifts?.length) {
        setSelectedShift(data[0].shifts[0]);
      }
    } catch {
      toast.error("Failed to load J&J shifts");
    }
  }, [selectedShift]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
      <ShiftSidebar
        groups={groups}
        expanded={expanded}
        setExpanded={setExpanded}
        selected={selectedShift}
        onSelect={setSelectedShift}
        onRefresh={load}
        testIdPrefix="jnj"
      />

      <div className="rounded-lg border hairline surface-1 min-h-[60vh] flex flex-col" data-testid="jnj-main">
        {!selectedShift && (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-500 py-20">
            Select a shift on the left.
          </div>
        )}
        {selectedShift && (
          <>
            <div className="border-b hairline flex items-center gap-1 px-2">
              <button onClick={() => setSubTab("qr")} data-testid="jnj-tab-qr"
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition
                  ${subTab === "qr" ? "border-red-500 text-red-200" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                <QrCode className="w-4 h-4 inline mr-1.5" /> QR Code
              </button>
              <button onClick={() => setSubTab("log")} data-testid="jnj-tab-log"
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition
                  ${subTab === "log" ? "border-red-500 text-red-200" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                <Clock4 className="w-4 h-4 inline mr-1.5" /> Today's Log
              </button>
            </div>
            {subTab === "qr"
              ? <QrTab shift={selectedShift} />
              : <LogTab shift={selectedShift} />}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MODE 2 — SUPERVISOR MANUAL (separate login)
// ============================================================================
function ManualMode({ isAdmin }) {
  const [supToken, setSupToken] = useState(() => localStorage.getItem(SUPERVISOR_TOKEN_KEY) || "");
  const [supervisor, setSupervisor] = useState(null);
  const [checking, setChecking] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);

  // Admins skip the supervisor login entirely and are auto-treated as a
  // virtual supervisor with access to ALL J&J shifts. The frontend signals
  // this by NOT sending the X-Supervisor-Token header (the backend resolves
  // the regular Bearer token instead).
  const headersForRequests = supToken
    ? { "X-Supervisor-Token": supToken }
    : {};   // empty → axios will still attach the Authorization bearer via api defaults

  useEffect(() => {
    (async () => {
      try {
        // Admins: hit the endpoint WITHOUT the sup token (uses Bearer)
        // Non-admins: only call if they have a sup token
        if (isAdmin) {
          const { data } = await api.get("/jnj/supervisor-me");
          setSupervisor({ ...data, is_admin: true });
        } else if (supToken) {
          const { data } = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-me`,
            { headers: { "X-Supervisor-Token": supToken } }
          );
          setSupervisor(data);
        }
      } catch {
        if (!isAdmin) {
          localStorage.removeItem(SUPERVISOR_TOKEN_KEY);
          setSupToken("");
        }
      } finally {
        setChecking(false);
      }
    })();
  }, [supToken, isAdmin]);

  const handleLogin = (token, sup) => {
    localStorage.setItem(SUPERVISOR_TOKEN_KEY, token);
    setSupToken(token);
    setSupervisor(sup);
  };

  const handleLogout = () => {
    localStorage.removeItem(SUPERVISOR_TOKEN_KEY);
    setSupToken("");
    setSupervisor(null);
  };

  if (checking) {
    return <div className="text-center text-sm text-slate-500 py-20">Checking session…</div>;
  }

  // Admins bypass the login screen entirely.
  if (!supervisor && !isAdmin) {
    return (
      <>
        <SupervisorLogin onLogin={handleLogin} isAdmin={isAdmin} onManageClick={() => setManageOpen(true)} />
        <ManageSupervisorsDialog open={manageOpen} onOpenChange={setManageOpen} />
      </>
    );
  }

  return (
    <>
      <ManualAttendance
        supervisor={supervisor}
        supToken={supToken}
        onLogout={handleLogout}
        isAdmin={isAdmin}
        onManageClick={() => setManageOpen(true)}
      />
      <ManageSupervisorsDialog open={manageOpen} onOpenChange={setManageOpen} />
    </>
  );
}

// ----------------------------------------------------------------------------
// Supervisor login form
// ----------------------------------------------------------------------------
function SupervisorLogin({ onLogin, isAdmin, onManageClick }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-login`,
        { username, password }
      );
      onLogin(data.token, data.supervisor);
      toast.success(`Logged in as ${data.supervisor.name}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border hairline surface-1 max-w-md mx-auto mt-10 p-8">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-5 h-5 text-amber-300" />
        <h3 className="text-lg font-semibold">Supervisor login</h3>
      </div>
      <p className="text-xs text-slate-500 mb-6 leading-relaxed">
        Enter your supervisor credentials to access manual attendance marking for
        Kenvue / Ceva shifts. This is a separate login from the main Mercy account.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400">Supervisor ID</Label>
          <Input value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g. ahmad.site" autoFocus autoComplete="username"
            className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="sup-login-username" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="sup-login-password" />
        </div>
        {err && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
            <div>{err}</div>
            {/^Invalid/i.test(err) && (
              <div className="mt-1.5 text-[11px] text-amber-200/80">
                Tip: supervisor accounts are separate from staff accounts.
                Ask an admin to add you under <strong>J&J → Manage supervisor accounts</strong> (use "Linked to staff user" if you already have a staff login).
              </div>
            )}
          </div>
        )}
        <Button type="submit" disabled={busy || !username || !password}
          className="w-full bg-amber-600 hover:bg-amber-500 text-amber-50" data-testid="sup-login-submit">
          <KeyRound className="w-4 h-4 mr-1.5" /> {busy ? "Signing in…" : "Sign in as supervisor"}
        </Button>
      </form>
      {isAdmin && (
        <div className="mt-6 pt-4 border-t hairline text-center">
          <button onClick={onManageClick} className="text-xs text-slate-500 hover:text-slate-200 inline-flex items-center gap-1"
            data-testid="sup-manage-link">
            <Settings2 className="w-3 h-3" /> Manage supervisor accounts (admin)
          </button>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Manual attendance grid (after supervisor login)
// ----------------------------------------------------------------------------
function ManualAttendance({ supervisor, supToken, onLogout, isAdmin, onManageClick }) {
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [selectedShift, setSelectedShift] = useState(null);
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [addWorkerOpen, setAddWorkerOpen] = useState(false);
  const [deleteCtx, setDeleteCtx] = useState(null); // { worker_id, name }
  // Share dialog ctx — { shift, date } used by the WhatsApp/PDF dialog
  const [shareCtx, setShareCtx] = useState(null);

  // Helper: pick the right axios headers depending on whether we're acting
  // as an admin (Bearer) or a registered supervisor (X-Supervisor-Token).
  const supHeaders = supToken ? { "X-Supervisor-Token": supToken } : {};
  const adminAuthHeaders = () => {
    // For admin mode, attach Bearer manually so the supervisor endpoints
    // can use the existing _resolve_supervisor_context fallback.
    if (supToken) return supHeaders;
    const t = localStorage.getItem("mercy_token") || "";
    return t ? { "Authorization": `Bearer ${t}` } : {};
  };

  // Load shifts list (filtered to supervisor's allowed; admins see all)
  useEffect(() => {
    (async () => {
      try {
        const { data: all } = await api.get("/jnj/companies");
        const allowed = supervisor?.companies_allowed || [];
        const filtered = (all || []).map((g) => ({
          ...g,
          shifts: g.shifts.filter((s) => allowed.includes(s.id)),
        })).filter((g) => g.shifts.length > 0);
        setGroups(filtered);
        const init = {};
        filtered.forEach((g) => { init[g.parent] = true; });
        setExpanded(init);
        if (filtered.length && filtered[0].shifts.length) {
          setSelectedShift(filtered[0].shifts[0]);
        }
      } catch (e) {
        toast.error("Failed to load shifts");
      }
    })();
  }, [supervisor?.companies_allowed]);

  const load = useCallback(async () => {
    if (!selectedShift) return;
    try {
      const { data: d } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-attendance`,
        { params: { company_id: selectedShift.id, date }, headers: adminAuthHeaders() }
      );
      setData(d);
    } catch (e) {
      if (e?.response?.status === 401 && !isAdmin) onLogout();
      else toast.error(e?.response?.data?.detail || "Failed to load attendance");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShift, date, supToken, isAdmin, onLogout]);

  useEffect(() => { load(); }, [load]);

  // Statuses that need a reason. Prefer the server's authoritative list
  // (sent in `data.statuses_note_required`); fall back to the local
  // constant if the field is missing.
  const noteRequiredSet = React.useMemo(() => {
    const arr = data?.statuses_note_required;
    if (Array.isArray(arr) && arr.length) return new Set(arr);
    return NOTE_REQUIRED_STATUSES;
  }, [data]);

  // Dialog state — opens for any note-required status. Carries the status
  // + worker into the dialog so the submit handler can re-call /supervisor-mark
  // with the full payload (notes + optional MC attachment).
  const [noteDlg, setNoteDlg] = useState(null);

  // Low-level mark — used by both quick-mark (P / AL / NG) AND the note dialog.
  const markRaw = async (entries) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-mark`,
        { company_id: selectedShift.id, date, entries },
        { headers: adminAuthHeaders() }
      );
      load();
      return { ok: true };
    } catch (e) {
      if (e?.response?.status === 401 && !isAdmin) { onLogout(); return { ok: false }; }
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Failed to save";
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const markOne = (worker_id, status) => {
    // Note-required statuses go through the dialog; the rest fire directly.
    if (noteRequiredSet.has(status)) {
      const worker = (data?.workers || []).find((w) => w.worker_id === worker_id);
      setNoteDlg({
        worker_id, worker_name: worker?.name || "",
        status,
        existing_notes: worker?.notes || "",
        existing_attachment: worker?.has_mc_attachment ? worker?.mc_attachment_filename : "",
      });
      return;
    }
    markRaw([{ worker_id, status }]);
  };

  const resetDevice = async (worker_id, worker_name) => {
    if (!window.confirm(
      `Reset device for "${worker_name}"?\n\nNext time they scan the QR, the new phone will be saved as theirs.`
    )) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-reset-device/${worker_id}`,
        {},
        { headers: adminAuthHeaders() }
      );
      toast.success("Device reset — they can claim from a new phone now");
      load();
    } catch (e) {
      if (e?.response?.status === 401 && !isAdmin) onLogout();
      else toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const resetFace = async (worker_id, worker_name) => {
    if (!window.confirm(
      `Reset face enrolment for "${worker_name}"?\n\nThey will be asked to re-enrol on their next scan. Their monthly QR-fallback counter does NOT reset.`
    )) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jnj/workers/${worker_id}/face-reset`,
        {},
        { headers: adminAuthHeaders() }
      );
      toast.success("Face enrolment cleared — next scan will re-enrol");
      load();
    } catch (e) {
      if (e?.response?.status === 401 && !isAdmin) onLogout();
      else toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // Admin-only: hard-terminate a worker (sets status='terminated' + reason).
  const removeWorker = async (reason) => {
    if (!deleteCtx) return;
    try {
      await api.delete(`/roster/workers/${deleteCtx.worker_id}`, {
        params: { reason: (reason || "").trim() },
      });
      toast.success("Worker terminated");
      setDeleteCtx(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // Stream the MC document for a worker. Built via fetch because the
  // attachment endpoint needs auth headers and we want to open the
  // result in a new tab as a blob: URL (bypasses caching weirdness on
  // some browsers and keeps the bearer token out of any logs).
  const viewMcAttachment = async (worker_id) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-mc-attachment` +
          `?company_id=${encodeURIComponent(selectedShift.id)}` +
          `&worker_id=${encodeURIComponent(worker_id)}` +
          `&date=${encodeURIComponent(date)}`,
        { headers: adminAuthHeaders() }
      );
      if (!res.ok) {
        toast.error(res.status === 404 ? "No attachment found" : "Failed to load attachment");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Best-effort cleanup — most browsers keep the blob alive for the
      // duration of the new tab.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Failed to load attachment");
    }
  };

  // Stream a worker's resignation / termination letter (Resign / Terminated
  // terminal statuses). Admin & supervisor both have read access; the
  // backend enforces shift-scoping for supervisors.
  const viewTerminationLetter = async (worker_id) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-termination-letter/${worker_id}`,
        { headers: adminAuthHeaders() }
      );
      if (!res.ok) {
        toast.error(res.status === 404 ? "No letter on file" : "Failed to load letter");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Failed to load letter");
    }
  };

  // Admin-only — bring a resigned / terminated worker back to active.
  const reinstateWorker = async (worker_id, worker_name) => {
    if (!window.confirm(
      `Reinstate "${worker_name}"?\n\n` +
      `They'll go back to the active list. Their termination letter stays on file as audit.`
    )) return;
    try {
      await api.post(`/roster/workers/${worker_id}/reinstate`);
      toast.success(`${worker_name} reinstated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      {/* Supervisor / Admin header strip */}
      <div className={`flex items-center gap-3 mb-4 px-3 py-2 rounded-lg border
        ${isAdmin ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
        {isAdmin ? <ShieldCheck className="w-4 h-4 text-emerald-300" /> : <Lock className="w-4 h-4 text-amber-300" />}
        <div className="text-sm">
          {isAdmin ? (
            <>
              <span className="text-slate-400">Admin view</span> ·{" "}
              <span className="font-semibold text-emerald-200">{supervisor?.name || "Admin"}</span>
              <span className="text-slate-500 text-[11px] ml-2">(access to all J&J shifts)</span>
            </>
          ) : (
            <>
              <span className="text-slate-400">Supervisor:</span>{" "}
              <span className="font-semibold text-amber-200">{supervisor.name}</span>
              <span className="text-slate-500 num text-[11px] ml-2">@{supervisor.username}</span>
            </>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isAdmin && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400"
              onClick={onManageClick} data-testid="sup-manage-btn">
              <Settings2 className="w-3.5 h-3.5 mr-1" /> Manage supervisors
            </Button>
          )}
          {!isAdmin && (
            <Button size="sm" variant="outline" className="h-7 border-amber-500/30 text-amber-200 text-xs"
              onClick={onLogout} data-testid="sup-logout-btn">
              <LogOutIcon className="w-3.5 h-3.5 mr-1" /> Log out
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        <ShiftSidebar groups={groups} expanded={expanded} setExpanded={setExpanded}
          selected={selectedShift} onSelect={setSelectedShift}
          testIdPrefix="sup" />

        <div className="rounded-lg border hairline surface-1 min-h-[60vh] flex flex-col">
          {!selectedShift && (
            <div className="text-center text-sm text-slate-500 py-20">
              You have no shifts assigned. Ask an admin to assign one.
            </div>
          )}
          {selectedShift && (
            <>
              <div className="px-4 py-3 border-b hairline flex items-center gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 num">
                    {selectedShift.name}
                  </div>
                  <div className="text-base font-semibold">{selectedShift.shift_label}</div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => setShareCtx({ shift: selectedShift, date })}
                    className="h-8 border-emerald-700/40 text-emerald-200 hover:bg-emerald-500/10"
                    data-testid="sup-share-btn">
                    <Share2 className="w-3.5 h-3.5 mr-1" /> Share
                  </Button>
                  <Button size="sm" onClick={() => setAddWorkerOpen(true)}
                    className="h-8 bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
                    data-testid="sup-add-worker-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add worker
                  </Button>
                  <Label className="text-xs text-slate-500">Date</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    className="bg-[#0a0e1a] border-slate-800 h-8 w-[160px] num"
                    data-testid="sup-date" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto max-h-[65vh] p-2 space-y-1">
                {data?.workers?.length === 0 && (
                  <div className="text-center text-sm text-slate-500 py-12">No workers in this shift.</div>
                )}
                {data?.workers?.map((w, i) => (
                  <SupRow key={w.worker_id} w={w} idx={i}
                    onMark={markOne}
                    onResetDevice={resetDevice}
                    onResetFace={resetFace}
                    onViewAttachment={viewMcAttachment}
                    onDelete={isAdmin ? () => setDeleteCtx({ worker_id: w.worker_id, name: w.name }) : null}
                  />
                ))}

                {/* Terminated / Resigned workers — separate section below the active list.
                    Auto-appears on every date on/after the worker's termination_effective_date.
                    Admin gets a Reinstate action; supervisors can only view. */}
                {data?.terminated_workers?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-dashed border-slate-700 space-y-1">
                    <div className="px-1 pb-2 text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
                      <span>⛔ Terminated / Resigned</span>
                      <span className="text-slate-600 normal-case tracking-normal">
                        ({data.terminated_workers.length}) — kept visible until admin reinstates
                      </span>
                    </div>
                    {data.terminated_workers.map((tw, i) => (
                      <TerminatedRow key={tw.worker_id} w={tw} idx={i}
                        isAdmin={isAdmin}
                        onViewLetter={viewTerminationLetter}
                        onReinstate={reinstateWorker}
                      />
                    ))}
                  </div>
                )}
              </div>

              {data && (
                <div className="px-4 py-2 border-t hairline text-[11px] num text-slate-500 flex items-center justify-between">
                  <span>{data.workers?.filter((w) => w.status).length || 0} of {data.workers?.length || 0} marked</span>
                  <span>You can revisit any time before midnight</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AddWorkerDialog
        open={addWorkerOpen}
        onOpenChange={setAddWorkerOpen}
        supToken={supToken}
        company_id={selectedShift?.id}
        onSaved={() => { setAddWorkerOpen(false); load(); }}
      />

      <TerminateWorkerDialog
        open={!!deleteCtx}
        onOpenChange={(v) => { if (!v) setDeleteCtx(null); }}
        ctx={deleteCtx}
        onConfirm={removeWorker}
      />

      <StatusNoteDialog
        open={!!noteDlg}
        ctx={noteDlg}
        company_id={selectedShift?.id}
        date={date}
        onClose={() => setNoteDlg(null)}
        onSubmit={async (entry) => {
          const res = await markRaw([entry]);
          if (res.ok) {
            toast.success(`Marked ${entry.status}`);
            setNoteDlg(null);
          }
        }}
      />

      <ShareDialog
        open={!!shareCtx}
        ctx={shareCtx}
        adminAuthHeaders={adminAuthHeaders}
        data={data}
        onClose={() => setShareCtx(null)}
      />
    </>
  );
}

function SupRow({ w, idx, onMark, onResetDevice, onResetFace, onDelete, onViewAttachment }) {
  return (
    <div className="rounded border hairline bg-[#0a0e1a]/40 px-3 py-2.5 flex flex-col gap-2" data-testid={`sup-worker-${w.worker_id}`}>
      {/* Row 1: index + full name + badges + delete (top-right) */}
      <div className="flex items-start gap-2">
        <span className="text-slate-600 num text-xs w-6 text-right flex-shrink-0 pt-0.5">{idx + 1}.</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium flex flex-wrap items-center gap-1.5 break-words">
            <span className="text-slate-100">{w.name}</span>
            {w.is_bound && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 num"
                    title={w.bound_at ? `Bound ${new Date(w.bound_at).toLocaleDateString()}` : "Bound"}>
                📱 phone
              </span>
            )}
            {w.is_face_enrolled && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30 num"
                    title={w.face_enrolled_at
                      ? `Face baseline saved ${new Date(w.face_enrolled_at).toLocaleDateString()}`
                      : "Face enrolled"}>
                👁 face
              </span>
            )}
            {w.fallbacks_used > 0 && (
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded num
                  ${w.fallbacks_remaining === 0
                    ? "bg-red-500/15 text-red-300 border border-red-500/30"
                    : "bg-amber-500/15 text-amber-300 border border-amber-500/30"}`}
                    title="QR-only fallbacks used this month">
                {w.fallbacks_used}/{w.fallbacks_used + w.fallbacks_remaining}⚠
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 num mt-0.5">
            {w.status ? (
              <span className={`inline-block px-1.5 py-0.5 rounded border ${STATUS_PILL[w.status] || ""}`}>
                {w.status}{w.check_in && ` · ${w.check_in}`}
              </span>
            ) : <span className="text-slate-600">not marked</span>}
            {w.marked_by_name && <span className="ml-2 text-slate-600">by {w.marked_by_name}</span>}
            {w.has_mc_attachment && (
              <button onClick={() => onViewAttachment && onViewAttachment(w.worker_id)}
                className="ml-2 inline-flex items-center gap-0.5 text-violet-300 hover:text-violet-200 underline-offset-2 hover:underline"
                data-testid={`sup-mc-attachment-${w.worker_id}`}
                title={w.mc_attachment_filename || "MC attachment"}>
                📎 {w.mc_attachment_filename || "MC"}
              </button>
            )}
          </div>
          {w.notes && (
            <div className="text-[10px] text-slate-500 italic mt-0.5 break-words"
                 title={w.notes}
                 data-testid={`sup-note-${w.worker_id}`}>
              “{w.notes}”
            </div>
          )}
        </div>
        {onDelete && (
          <button onClick={onDelete}
            data-testid={`sup-delete-${w.worker_id}`}
            title="Admin only — terminate worker (kept in monthly reports)"
            className="flex-shrink-0 px-2 py-1 rounded text-[10px] font-semibold num bg-red-700 hover:bg-red-600 text-red-50 transition flex items-center gap-1">
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
      {/* Row 2: action buttons (status marks + reset device/face) */}
      <div className="flex items-center gap-1 flex-wrap pl-8">
        {STATUS_OPTIONS.map((s) => (
          <button key={s.key}
            onClick={() => onMark(w.worker_id, s.key)}
            data-testid={`sup-mark-${w.worker_id}-${s.key}`}
            className={`px-2 py-1 rounded text-[10px] font-semibold num transition active:scale-95 ${s.cls} ${w.status === s.key ? "ring-2 ring-white/40" : "opacity-70 hover:opacity-100"}`}
            title={s.key}>
            {s.short}
          </button>
        ))}
        {w.is_bound && onResetDevice && (
          <button onClick={() => onResetDevice(w.worker_id, w.name)}
            data-testid={`sup-reset-${w.worker_id}`}
            title="Reset phone binding — they can claim from a new phone next scan"
            className="ml-1 px-2 py-1 rounded text-[10px] font-semibold num bg-slate-700 hover:bg-slate-600 text-slate-200 transition">
            Reset 📱
          </button>
        )}
        {w.is_face_enrolled && onResetFace && (
          <button onClick={() => onResetFace(w.worker_id, w.name)}
            data-testid={`sup-reset-face-${w.worker_id}`}
            title="Reset face — next scan re-enrols a fresh baseline"
            className="ml-1 px-2 py-1 rounded text-[10px] font-semibold num bg-sky-700 hover:bg-sky-600 text-sky-50 transition">
            Reset 👁
          </button>
        )}
      </div>
    </div>
  );
}

// TerminatedRow — visually distinct, opaque-greyed row for workers whose
// employment has ended (Resign or Terminated). They auto-roll forward to
// every subsequent day, so admins see a persistent reminder until someone
// either reinstates them or hard-deletes them from the shift roster.
function TerminatedRow({ w, idx, isAdmin, onViewLetter, onReinstate }) {
  const kindLabel = w.kind === "resigned" ? "Resigned" : "Terminated";
  return (
    <div className="rounded border border-slate-800/60 bg-slate-900/40 px-3 py-2 flex items-center gap-3 opacity-90"
         data-testid={`sup-terminated-${w.worker_id}`}>
      <span className="text-slate-700 num text-xs w-6 text-right">{idx + 1}.</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate text-slate-300 flex items-center gap-1.5">
          <span className="line-through decoration-slate-600">{w.name}</span>
          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border num ${STATUS_PILL[w.status] || STATUS_PILL.Terminated}`}>
            {kindLabel}
          </span>
          {w.termination_effective_date && (
            <span className="text-[10px] text-slate-500 num">
              since {w.termination_effective_date}
            </span>
          )}
        </div>
        {w.notes && (
          <div className="text-[10px] text-slate-500 italic mt-0.5 truncate" title={w.notes}>
            “{w.notes}”
            {w.marked_by_name && <span className="ml-1 not-italic text-slate-600">— {w.marked_by_name}</span>}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-wrap justify-end">
        {w.has_termination_letter && (
          <button onClick={() => onViewLetter(w.worker_id)}
            className="px-2 py-1 rounded text-[10px] font-semibold num bg-slate-700 hover:bg-slate-600 text-slate-100 transition flex items-center gap-1"
            data-testid={`sup-letter-${w.worker_id}`}
            title={w.termination_letter_filename || "View letter"}>
            📎 Letter
          </button>
        )}
        {isAdmin && (
          <button onClick={() => onReinstate(w.worker_id, w.name)}
            className="px-2 py-1 rounded text-[10px] font-semibold num bg-emerald-700 hover:bg-emerald-600 text-emerald-50 transition"
            data-testid={`sup-reinstate-${w.worker_id}`}
            title="Bring the worker back to the active list">
            ↺ Reinstate
          </button>
        )}
      </div>
    </div>
  );
}


// Admin-only termination dialog (reused inside the supervisor manual grid).
function TerminateWorkerDialog({ open, onOpenChange, ctx, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!ctx) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-200">
            <Trash2 className="w-5 h-5" /> Terminate "{ctx.name}"?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm text-slate-400">
          <p>
            They will be marked as <strong className="text-red-300">terminated</strong>.
            They'll keep appearing in past monthly reports (showing exactly when they stopped working)
            and stop appearing in daily attendance.
          </p>
          <div>
            <Label className="text-xs text-slate-400">Why are they leaving?</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. ran away, contract ended, fired, transferred"
              className="bg-[#0a0e1a] border-slate-800 mt-1"
              data-testid="sup-terminate-reason"
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onConfirm(reason)}
            className="bg-red-600 hover:bg-red-500"
            data-testid="sup-terminate-confirm">
            <Trash2 className="w-4 h-4 mr-1" /> Mark terminated
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Supervisor "Add worker" dialog — supervisor can register new workers (e.g.
// new hire walked in today) but CANNOT delete them. Deletion is admin-only.
function AddWorkerDialog({ open, onOpenChange, supToken, company_id, onSaved }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setCode(""); setBusy(false); }
  }, [open]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      // Admin path uses Bearer (no supToken); supervisor path uses X-Supervisor-Token.
      const headers = supToken
        ? { "X-Supervisor-Token": supToken }
        : (localStorage.getItem("mercy_token")
            ? { "Authorization": `Bearer ${localStorage.getItem("mercy_token")}` }
            : {});
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-worker`,
        { company_id, name: name.trim(), code: code.trim() },
        { headers }
      );
      toast.success(`${name} added`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-300" /> Add new worker
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-400">Full name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              autoFocus
              className="bg-[#0a0e1a] border-slate-800 mt-1"
              placeholder="e.g. ABDUL RAHMAN BIN HASSAN"
              data-testid="sup-addworker-name" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Code (optional)</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)}
              className="bg-[#0a0e1a] border-slate-800 mt-1 num"
              placeholder="e.g. K-042"
              data-testid="sup-addworker-code" />
          </div>
          <p className="text-[11px] text-slate-500 leading-snug">
            Only admins can remove workers. If this worker leaves later,
            ask an admin to mark them as terminated — their attendance history
            stays in the reports.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
            data-testid="sup-addworker-save">
            <Plus className="w-4 h-4 mr-1" /> {busy ? "Adding…" : "Add worker"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Manage supervisors (admin-only dialog)
// ============================================================================
function ManageSupervisorsDialog({ open, onOpenChange }) {
  const [list, setList] = useState([]);
  const [shifts, setShifts] = useState([]); // flat list of all Kenvue/Ceva shifts
  const [staffUsers, setStaffUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blankForm());

  function blankForm() {
    return { name: "", username: "", password: "", linked_user_id: "", companies_allowed: [] };
  }

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const [a, b, c] = await Promise.all([
        api.get("/jnj/supervisors"),
        api.get("/jnj/companies"),
        api.get("/staff"),
      ]);
      setList(a.data || []);
      const flat = [];
      (b.data || []).forEach((g) => g.shifts.forEach((s) => flat.push({ ...s, parent: g.parent })));
      setShifts(flat);
      setStaffUsers(c.data || []);
    } catch {}
  }, [open]);
  useEffect(() => { load(); }, [load]);

  const toggleShift = (id) => {
    setForm((f) => ({
      ...f,
      companies_allowed: f.companies_allowed.includes(id)
        ? f.companies_allowed.filter((x) => x !== id)
        : [...f.companies_allowed, id],
    }));
  };

  const create = async () => {
    if (!form.name || !form.username) { toast.error("Name & username required"); return; }
    if (!form.linked_user_id && (!form.password || form.password.length < 4)) {
      toast.error("Password (4+ chars) required for standalone supervisor"); return;
    }
    try {
      const payload = {
        name: form.name,
        username: form.username.trim().toLowerCase(),
        companies_allowed: form.companies_allowed,
      };
      if (form.linked_user_id) payload.linked_user_id = form.linked_user_id;
      else payload.password = form.password;
      await api.post("/jnj/supervisors", payload);
      toast.success("Supervisor created");
      setForm(blankForm()); setAdding(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supervisor "${s.name}"?`)) return;
    try {
      await api.delete(`/jnj/supervisors/${s.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/jnj/supervisors/${s.id}`, { is_active: !s.is_active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-3xl max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-amber-300" /> Manage supervisor accounts
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          {/* Existing list */}
          <div className="space-y-1.5">
            {list.length === 0 && !adding && (
              <div className="text-center py-6 text-sm text-slate-500">
                No supervisors yet. Add one below.
              </div>
            )}
            {list.map((s) => (
              <div key={s.id} className="rounded-lg border hairline bg-[#0a0e1a]/40 p-3 flex items-center gap-3"
                data-testid={`sup-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {s.name}
                    {s.linked_user_id && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                        linked → {s.linked_user_name}
                      </span>
                    )}
                    {!s.is_active && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">
                        inactive
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 num mt-0.5">
                    @{s.username} · {s.companies_allowed.length} shift{s.companies_allowed.length !== 1 ? "s" : ""}
                    {s.last_login && ` · last login ${new Date(s.last_login).toLocaleString("en-GB", {day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}`}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs"
                  onClick={() => toggleActive(s)}>
                  {s.is_active ? "Disable" : "Enable"}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-300 hover:text-red-200"
                  onClick={() => remove(s)} data-testid={`sup-delete-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>

          {!adding ? (
            <Button variant="outline" className="border-slate-700 w-full"
              onClick={() => setAdding(true)} data-testid="sup-add-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add new supervisor
            </Button>
          ) : (
            <div className="rounded-lg border hairline bg-emerald-500/[0.03] border-emerald-500/20 p-4 space-y-3"
              data-testid="sup-add-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-400">Display name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Ahmad Site Supervisor"
                    className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="sup-form-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Username (login id)</Label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="e.g. ahmad.site"
                    className="bg-[#0a0e1a] border-slate-800 mt-1 num" data-testid="sup-form-username" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-400">Account type</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    onClick={() => setForm({ ...form, linked_user_id: "", password: "" })}
                    className={`text-left rounded border px-3 py-2 text-xs ${!form.linked_user_id ? "border-emerald-500 bg-emerald-500/10" : "border-slate-800 bg-[#0a0e1a]"}`}>
                    <div className="font-semibold">Standalone</div>
                    <div className="text-slate-500 mt-0.5">Their own password</div>
                  </button>
                  <button
                    onClick={() => setForm({ ...form, linked_user_id: staffUsers[0]?.id || "", password: "" })}
                    className={`text-left rounded border px-3 py-2 text-xs ${form.linked_user_id ? "border-blue-500 bg-blue-500/10" : "border-slate-800 bg-[#0a0e1a]"}`}>
                    <div className="font-semibold">Linked to staff user</div>
                    <div className="text-slate-500 mt-0.5">Uses staff password</div>
                  </button>
                </div>
              </div>

              {form.linked_user_id ? (
                <div>
                  <Label className="text-xs text-slate-400">Linked staff user</Label>
                  <select value={form.linked_user_id} onChange={(e) => setForm({ ...form, linked_user_id: e.target.value })}
                    className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-1"
                    data-testid="sup-form-linked">
                    {staffUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.display_name} ({u.username})</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <Label className="text-xs text-slate-400">Password (4+ chars)</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="sup-form-password" />
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-400">Allowed shifts</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {shifts.map((s) => {
                    const checked = form.companies_allowed.includes(s.id);
                    return (
                      <label key={s.id} className={`flex items-center gap-2 cursor-pointer rounded border px-2 py-1.5 text-xs
                          ${checked ? "border-emerald-500 bg-emerald-500/5" : "border-slate-800 bg-[#0a0e1a]"}`}
                        data-testid={`sup-form-shift-${s.id}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleShift(s.id)} />
                        <span className="flex-1">
                          <span className="text-slate-300">{s.parent}</span>{" "}
                          <span className="text-slate-500">· {s.shift_label}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Button variant="ghost" size="sm" className="text-xs"
                  onClick={() => { setAdding(false); setForm(blankForm()); }}>
                  Cancel
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 ml-auto"
                  onClick={create} data-testid="sup-form-create">
                  Create supervisor
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Shared shift sidebar
// ============================================================================
function ShiftSidebar({ groups, expanded, setExpanded, selected, onSelect, onRefresh, testIdPrefix = "jnj" }) {
  return (
    <div className="rounded-lg border hairline surface-1 overflow-hidden self-start" data-testid={`${testIdPrefix}-sidebar`}>
      <div className="px-3 py-2 border-b hairline text-xs uppercase tracking-wider text-slate-500 num flex items-center justify-between">
        <span>Shifts</span>
        {onRefresh && (
          <button onClick={onRefresh} className="text-slate-400 hover:text-white">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {groups.length === 0 && (
        <div className="text-xs text-slate-500 text-center py-8 px-3">
          No shifts available.
        </div>
      )}
      {groups.map((g) => (
        <div key={g.parent} className="border-b border-slate-900/80 last:border-b-0">
          <button
            onClick={() => setExpanded((e) => ({ ...e, [g.parent]: !e[g.parent] }))}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-left hover:bg-white/[0.02]"
            data-testid={`${testIdPrefix}-parent-${g.parent}`}>
            {expanded[g.parent]
              ? <ChevronDown className="w-4 h-4 text-slate-500" />
              : <ChevronRight className="w-4 h-4 text-slate-500" />}
            <span className="text-sm font-semibold flex-1">{g.parent}</span>
            <span className="text-[10px] num text-slate-500">
              {g.shifts.reduce((a, s) => a + (s.events_today || 0), 0)} scans
            </span>
          </button>
          {expanded[g.parent] && g.shifts.map((s) => (
            <button
              key={s.id}
              onClick={() => onSelect(s)}
              data-testid={`${testIdPrefix}-shift-${s.id}`}
              className={`w-full pl-9 pr-3 py-2 flex items-center gap-2 text-left hover:bg-white/[0.02] border-l-2
                ${selected?.id === s.id ? "border-red-500 bg-red-500/5" : "border-transparent"}`}>
              {s.shift_label?.includes("Red")
                ? <Sun className="w-3.5 h-3.5 text-red-400" />
                : <Moon className="w-3.5 h-3.5 text-sky-400" />}
              <span className="text-sm flex-1">{s.shift_label || s.name}</span>
              <span className="text-[10px] num text-slate-500">{s.workers}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// QR TAB (was QrTab in v1) — big rotating QR + live feed
// ============================================================================
function QrTab({ shift }) {
  const [qr, setQr] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [events, setEvents] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);

  const refreshQr = useCallback(async () => {
    try {
      const { data } = await api.post("/jnj/qr-token", { company_id: shift.id });
      // Override the backend-built URL with one anchored to the CURRENT
      // origin so a QR generated in preview routes back to preview, in
      // production routes back to production, etc. (Avoids the worker
      // being sent to a stale APP_PUBLIC_URL if the backend env var is
      // misconfigured for the environment.)
      const localUrl = `${window.location.origin}/clock-in?t=${data.token}`;
      setQr({ ...data, url: localUrl });
    } catch { toast.error("Could not generate QR"); }
  }, [shift.id]);
  useEffect(() => { refreshQr(); }, [refreshQr]);
  useEffect(() => {
    if (!qr) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(qr.expires_at) - new Date()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) refreshQr();
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [qr, refreshQr]);

  // Close fullscreen on Esc; lock body scroll while open.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/jnj/events", { params: { company_id: shift.id } });
      setEvents(data);
    } catch {}
  }, [shift.id]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    const t = setInterval(loadEvents, 10_000);
    return () => clearInterval(t);
  }, [loadEvents]);

  const checkedIn = events?.workers?.filter((w) => w.events.length > 0) || [];
  const pending = events?.workers?.filter((w) => w.events.length === 0) || [];
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-0 flex-1">
      <div className="p-6 border-r hairline flex flex-col items-center justify-center relative" data-testid="jnj-qr-panel">
        {/* Top-right enlarge button — opens fullscreen QR for monitor display */}
        {qr && (
          <button
            onClick={() => setFullscreen(true)}
            title="Show QR fullscreen on monitor"
            className="absolute top-3 right-3 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs flex items-center gap-1.5 transition"
            data-testid="jnj-qr-fullscreen-btn"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Fullscreen</span>
          </button>
        )}
        <div className="text-center mb-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 num">{shift.name}</div>
          <div className="text-3xl font-bold mt-1">Scan to clock in</div>
        </div>
        {qr ? (
          <>
            <div className="bg-white p-4 rounded-xl shadow-lg" data-testid="jnj-qr-image">
              <QRCodeCanvas value={qr.url} size={300} level="H" includeMargin={false} />
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <Clock4 className="w-4 h-4 text-slate-400" />
              <span className="num">Refreshes in</span>
              <span className={`num font-bold text-base px-2 py-0.5 rounded ${secondsLeft < 60 ? "bg-amber-500/20 text-amber-200" : "bg-slate-700/40 text-slate-200"}`}
                data-testid="jnj-countdown">{mm}:{ss}</span>
              <Button size="sm" variant="outline" onClick={refreshQr}
                className="ml-2 border-slate-700 text-xs h-7" data-testid="jnj-refresh-now">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Now
              </Button>
            </div>
            <div className="mt-3 text-[11px] text-slate-500 text-center max-w-xs leading-relaxed">
              <MonitorSmartphone className="w-3.5 h-3.5 inline mr-1" />
              Display this QR on a screen at the entrance.
              Workers scan with their phone camera → tap their name → done.
            </div>
            <div className="mt-2 num text-[10px] text-slate-600 truncate max-w-sm">{qr.url}</div>
          </>
        ) : <div className="text-sm text-slate-400">Generating QR…</div>}
      </div>

      <div className="p-4 flex flex-col" data-testid="jnj-live-feed">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-emerald-300" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
            Today's check-ins
          </h3>
          <span className="text-xs num text-slate-500 ml-auto">
            {checkedIn.length} / {events?.workers?.length || 0} workers
          </span>
        </div>
        <div className="flex-1 overflow-y-auto max-h-[60vh] space-y-2 pr-1">
          {checkedIn.length === 0 && (
            <div className="text-xs text-slate-500 text-center py-8 border border-dashed hairline rounded-lg">
              Nobody has scanned yet today.
            </div>
          )}
          {checkedIn.map((w) => <WorkerEventRow key={w.worker_id} w={w} compact />)}
          {pending.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 pt-4 pb-1">
                Not yet checked in · {pending.length}
              </div>
              <div className="grid grid-cols-2 gap-1">
                {pending.map((w) => (
                  <div key={w.worker_id} className="text-xs text-slate-500 truncate py-0.5 px-2 rounded bg-slate-900/40">
                    · {w.worker_name}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Fullscreen overlay — covers everything; tap anywhere or press Esc to close */}
      {fullscreen && qr && (
        <FullscreenQR
          qr={qr}
          shift={shift}
          secondsLeft={secondsLeft}
          onRefresh={refreshQr}
          onClose={() => setFullscreen(false)}
        />
      )}
    </div>
  );
}

// Fullscreen QR for wall-monitor display. Pure presentation — refreshes the
// same QR state held by QrTab via the onRefresh prop, so closing doesn't
// reset the rotation timer.
function FullscreenQR({ qr, shift, secondsLeft, onRefresh, onClose }) {
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  // QR size = ~80% of the shorter viewport dimension so it fills the screen
  const [size, setSize] = useState(() =>
    Math.min(window.innerWidth, window.innerHeight) * 0.7);
  useEffect(() => {
    const onResize = () => setSize(Math.min(window.innerWidth, window.innerHeight) * 0.7);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[200] bg-slate-950 flex flex-col items-center justify-center p-6"
      data-testid="jnj-qr-fullscreen"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Top bar with close + shift name + countdown */}
      <div className="absolute top-0 left-0 right-0 px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 num">{shift.name}</div>
          <div className="text-xl font-bold mt-0.5 text-slate-100">Scan to clock in</div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${secondsLeft < 60 ? "bg-amber-500/20 text-amber-200" : "bg-slate-800 text-slate-200"}`}>
            <Clock4 className="w-4 h-4" />
            <span className="num font-bold text-base">{mm}:{ss}</span>
          </div>
          <button
            onClick={onRefresh}
            title="Generate new QR now"
            className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center gap-1.5"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            title="Exit fullscreen (Esc)"
            className="px-3 py-1.5 rounded-lg bg-red-600/80 hover:bg-red-500 text-white flex items-center gap-1.5"
            data-testid="jnj-qr-fullscreen-close"
          >
            <X className="w-4 h-4" /> Exit
          </button>
        </div>
      </div>

      {/* Giant QR */}
      <div className="bg-white p-6 rounded-2xl shadow-2xl" data-testid="jnj-qr-fullscreen-image">
        <QRCodeCanvas value={qr.url} size={size} level="H" includeMargin={false} />
      </div>

      {/* Helper text bottom */}
      <div className="absolute bottom-6 left-0 right-0 text-center text-slate-500 text-sm">
        Open your phone camera <span className="text-slate-300 font-semibold">— point at the QR</span> — tap the link
        <span className="block mt-1 text-xs text-slate-600">Press Esc or click Exit to return</span>
      </div>
    </div>
  );
}

// ============================================================================
// LOG TAB
// ============================================================================
function LogTab({ shift }) {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addCtx, setAddCtx] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/jnj/events", { params: { company_id: shift.id, date } });
      setData(d);
    } catch {}
  }, [shift.id, date]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 flex-1 flex flex-col">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Label className="text-xs text-slate-500">Date</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="bg-[#0a0e1a] border-slate-800 h-8 w-[160px] num" data-testid="jnj-log-date" />
        <Button size="sm" variant="outline" onClick={load} className="h-8 border-slate-700">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
        <div className="ml-auto text-xs text-slate-500 num">
          {data ? `${data.total_events} events · ${data.workers.filter(w=>w.events.length).length} workers` : ""}
        </div>
      </div>
      <div className="overflow-y-auto max-h-[65vh] space-y-1.5">
        {data?.workers?.map((w) => (
          <WorkerEventRow key={w.worker_id} w={w}
            onAdd={() => { setAddCtx({ worker_id: w.worker_id, worker_name: w.worker_name }); setAddOpen(true); }}
            isAdmin={isAdmin}
            onDeleted={load} />
        ))}
      </div>
      <AddEventDialog open={addOpen} onOpenChange={setAddOpen}
        company_id={shift.id} ctx={addCtx}
        onSaved={() => { setAddOpen(false); load(); }} />
    </div>
  );
}

function WorkerEventRow({ w, onAdd, isAdmin, onDeleted, compact }) {
  const events = w.events || [];
  const isOnSite = events.length && events[events.length - 1].kind !== "out";
  const remove = async (eid) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/jnj/events/${eid}`);
      toast.success("Event removed"); onDeleted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="rounded-lg border hairline bg-[#0a0e1a]/40 p-2.5 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-2 h-2 rounded-full shrink-0"
            style={{ background: isOnSite ? "#10b981" : events.length ? "#94a3b8" : "#475569" }} />
          <span className="text-sm font-medium truncate">{w.worker_name}</span>
          {w.is_late && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/30 uppercase tracking-wider num">
              Late · {w.check_in_local}
            </span>
          )}
          {!events.length && <span className="text-[10px] text-slate-600 italic">no events</span>}
        </div>
        {events.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1 flex-wrap">
            {events.map((e) => {
              const M = EVENT_META[e.kind];
              const Icon = M?.icon;
              return (
                <span key={e.id}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] num bg-slate-800/60 ${M?.cls || ""}`}
                  title={`${M?.label} · ${e.scan_method === "qr" ? "QR scan" : `manual by ${e.scanned_by_name}`}`}>
                  {Icon && <Icon className="w-3 h-3" />}
                  {fmtTime(e.timestamp)}
                  {isAdmin && (
                    <button onClick={() => remove(e.id)} className="opacity-50 hover:opacity-100 hover:text-red-300 ml-0.5">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {onAdd && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-emerald-300" onClick={onAdd}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      )}
    </div>
  );
}

function AddEventDialog({ open, onOpenChange, company_id, ctx, onSaved }) {
  const [kind, setKind] = useState("in");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  useEffect(() => {
    if (open) {
      setKind("in");
      const d = new Date();
      setTime(`${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`);
      setNotes("");
    }
  }, [open]);
  const save = async () => {
    if (!ctx) return;
    const today = new Date();
    const [hh, mm] = (time || "00:00").split(":");
    today.setHours(parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, 0, 0);
    try {
      await api.post("/jnj/events", { worker_id: ctx.worker_id, company_id, kind, timestamp: today.toISOString(), notes });
      toast.success("Event added"); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle>Add event manually</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-sm text-slate-400">For <span className="text-slate-100 font-medium">{ctx?.worker_name}</span></div>
          <div>
            <Label className="text-xs text-slate-400">Kind</Label>
            <select value={kind} onChange={(e) => setKind(e.target.value)}
              className="w-full bg-[#0a0e1a] border border-slate-800 rounded h-9 px-2 text-sm mt-1">
              {KIND_ORDER.map((k) => (<option key={k} value={k}>{EVENT_META[k].label}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Time (today)</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              className="bg-[#0a0e1a] border-slate-800 mt-1 num" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
              className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} className="bg-red-600 hover:bg-red-500">Add event</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ----------------------------------------------------------------------------
// StatusNoteDialog — prompts the supervisor for a reason (and, when MC, an
// optional photo / PDF of the medical certificate) before applying any
// note-required attendance status. Submits the full payload via the
// parent's onSubmit({worker_id, status, notes, attachment?}).
// ----------------------------------------------------------------------------
function StatusNoteDialog({ open, ctx, onClose, onSubmit }) {
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);          // { filename, content_type, data_base64 }
  const [busy, setBusy] = useState(false);

  // Reset on each open
  useEffect(() => {
    if (open) {
      setNotes(ctx?.existing_notes || "");
      setFile(null);
      setBusy(false);
    }
  }, [open, ctx]);

  if (!ctx) return null;
  const isMc = ctx.status === "MC";
  const requiresAttachment = ATTACHMENT_REQUIRED_STATUSES.has(ctx.status);
  const showAttachmentField = isMc || requiresAttachment;

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Document must be under 10MB"); e.target.value = ""; return;
    }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result || "").split(",")[1] || "");
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    setFile({ filename: f.name, content_type: f.type || "application/octet-stream", data_base64: b64 });
  };

  const submit = async () => {
    const trimmed = notes.trim();
    if (!trimmed) { toast.error("Please write a short reason"); return; }
    // For Resign / Terminated the attachment is MANDATORY — backend will
    // 400 us if it's missing but we surface a friendlier message client-side.
    if (ATTACHMENT_REQUIRED_STATUSES.has(ctx.status) && !file && !ctx.existing_attachment) {
      const label = ctx.status === "Resign" ? "resignation letter" : "termination notice";
      toast.error(`Please attach the ${label}`);
      return;
    }
    setBusy(true);
    const entry = { worker_id: ctx.worker_id, status: ctx.status, notes: trimmed };
    if (file && (ctx.status === "MC" || ATTACHMENT_REQUIRED_STATUSES.has(ctx.status))) {
      entry.attachment = file;
    }
    await onSubmit(entry);
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
                     data-testid="status-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] border ${STATUS_PILL[ctx.status] || ""}`}>
              {ctx.status}
            </span>
            <span className="truncate">{ctx.worker_name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">
              Reason / note <span className="text-red-400">*</span>
            </Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={isMc
                ? "e.g. Fever, Klinik Dr Lim Sg Petani, 2 days MC"
                : ctx.status === "Late"
                  ? "e.g. Traffic jam on Karak highway"
                  : ctx.status === "Absent"
                    ? "e.g. Did not show up, no contact"
                    : "Why is this status being marked?"}
              className="w-full mt-1 bg-[#0a0e1a] border border-slate-800 rounded p-2 text-sm focus:outline-none focus:border-slate-600"
              data-testid="status-note-textarea" />
            <div className="text-[10px] text-slate-500 mt-1">
              Required so others know the context. This is saved with the attendance row.
            </div>
          </div>

          {showAttachmentField && (
            <div>
              <Label className="text-xs text-slate-400">
                {isMc ? (
                  <>MC document <span className="text-slate-600">(photo / PDF, optional)</span></>
                ) : ctx.status === "Resign" ? (
                  <>Resignation letter <span className="text-red-400">*</span></>
                ) : (
                  <>Termination notice <span className="text-red-400">*</span></>
                )}
              </Label>
              <input type="file" accept="image/*,application/pdf"
                onChange={onFileChange}
                className={`block w-full mt-1 text-xs text-slate-400 file:mr-2 file:rounded file:border-0 file:text-50 file:px-2 file:py-1 file:text-xs ${
                  isMc ? "file:bg-violet-600 hover:file:bg-violet-500 file:text-violet-50"
                       : ctx.status === "Resign"
                         ? "file:bg-orange-700 hover:file:bg-orange-600 file:text-orange-50"
                         : "file:bg-red-800 hover:file:bg-red-700 file:text-red-50"
                }`}
                data-testid="status-note-attachment" />
              {file ? (
                <div className="text-[11px] text-emerald-300 mt-1">✓ {file.filename}</div>
              ) : ctx.existing_attachment ? (
                <div className="text-[11px] text-slate-500 mt-1">
                  Currently attached: {ctx.existing_attachment} (leave blank to keep)
                </div>
              ) : null}
              {requiresAttachment && (
                <div className="text-[10px] text-amber-400 mt-1">
                  ⚠️ This worker will be marked {ctx.status === "Resign" ? "Resigned" : "Terminated"} from this day forward, until an admin reinstates them.
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className={
              isMc ? "bg-violet-600 hover:bg-violet-500"
                : ctx.status === "Resign" ? "bg-orange-700 hover:bg-orange-600"
                : ctx.status === "Terminated" ? "bg-red-800 hover:bg-red-700"
                : "bg-amber-600 hover:bg-amber-500"
            }
            data-testid="status-note-save">
            {busy ? "Saving..." : `Mark ${ctx.status}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ----------------------------------------------------------------------------
// ShareDialog — generates WhatsApp text + printable PDF view for a shift's
// daily attendance. Opens from the Supervisor Manual tab header. The
// WhatsApp text is fetched fresh from /api/jnj/supervisor-whatsapp so the
// formatting / totals are server-authoritative; the PDF view renders from
// the same `data` already loaded on the screen so it works offline if
// the user's connection drops while preparing to print.
// ----------------------------------------------------------------------------
function ShareDialog({ open, ctx, data, adminAuthHeaders, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("whatsapp"); // whatsapp | pdf

  useEffect(() => {
    if (!open || !ctx) return;
    setBusy(true);
    setText("");
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/jnj/supervisor-whatsapp`,
      {
        params: { company_id: ctx.shift.id, date: ctx.date },
        headers: adminAuthHeaders(),
      },
    ).then((r) => setText(r.data?.text || ""))
     .catch((e) => toast.error(e?.response?.data?.detail || "Could not build summary"))
     .finally(() => setBusy(false));
  }, [open, ctx, adminAuthHeaders]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied — paste into WhatsApp");
    } catch {
      toast.error("Copy failed — long-press the text to copy");
    }
  };

  const openWa = () => {
    if (!text) return;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const doPrint = () => {
    setTab("pdf");
    // Defer print until the print-only DOM has mounted in this render cycle
    setTimeout(() => window.print(), 250);
  };

  if (!open || !ctx) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg print:hidden"
          data-testid="jnj-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-4 h-4 text-emerald-300" />
              Share attendance — {ctx.shift?.shift_label || ctx.shift?.name}
              <span className="text-xs text-slate-500 num font-normal">({ctx.date})</span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-1 border-b border-slate-800 -mx-6 px-6 -mt-1 mb-2">
            <button onClick={() => setTab("whatsapp")}
              className={`px-3 py-2 text-xs border-b-2 -mb-px transition flex items-center gap-1.5
                ${tab === "whatsapp" ? "border-emerald-500 text-emerald-200" : "border-transparent text-slate-400"}`}
              data-testid="share-tab-whatsapp">
              <span>📲</span> WhatsApp
            </button>
            <button onClick={() => setTab("pdf")}
              className={`px-3 py-2 text-xs border-b-2 -mb-px transition flex items-center gap-1.5
                ${tab === "pdf" ? "border-emerald-500 text-emerald-200" : "border-transparent text-slate-400"}`}
              data-testid="share-tab-pdf">
              <span>📄</span> PDF / Print
            </button>
          </div>

          {tab === "whatsapp" ? (
            <>
              <div className="rounded-md border border-slate-800 bg-[#0a0e1a] p-3 max-h-[55vh] overflow-y-auto">
                {busy ? (
                  <div className="text-xs text-slate-500 py-6 text-center">Building summary…</div>
                ) : (
                  <pre className="text-[12px] leading-relaxed whitespace-pre-wrap font-sans"
                       data-testid="share-text">{text}</pre>
                )}
              </div>
              <DialogFooter className="mt-3 gap-2">
                <Button variant="ghost" onClick={onClose}>Close</Button>
                <Button variant="outline" onClick={copy} disabled={!text}
                  className="border-slate-700 text-slate-200"
                  data-testid="share-copy-btn">
                  Copy text
                </Button>
                <Button onClick={openWa} disabled={!text}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="share-whatsapp-btn">
                  📲 Open WhatsApp
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="text-xs text-slate-400 py-4 space-y-2">
                <p>Clicking <strong>Print PDF</strong> opens your browser's print dialog with a clean
                  one-page report. From there, choose <strong>Save as PDF</strong> as the destination
                  (works on Chrome, Safari, Edge, Firefox — desktop and mobile).</p>
                <p className="text-slate-500 text-[11px]">
                  Tip: in WhatsApp on phone, attach the saved PDF directly from your Files / Downloads.
                </p>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={onClose}>Close</Button>
                <Button onClick={doPrint}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="share-print-btn">
                  📄 Print PDF
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Print-only DOM. Hidden on screen; window.print() reveals it. */}
      <PrintReport shift={ctx.shift} date={ctx.date} data={data} />
    </>
  );
}

// ----------------------------------------------------------------------------
// PrintReport — print-only DOM block, light theme, optimised for one-page A4.
// Uses CSS @media print to hide the rest of the app and show only this.
// ----------------------------------------------------------------------------
function PrintReport({ shift, date, data }) {
  const dt = new Date(date + "T00:00:00");
  const dateStr = dt.toLocaleDateString("en-GB", {
    day: "2-digit", month: "long", year: "numeric", weekday: "long",
  });

  // Group active workers by status — match the WhatsApp grouping order.
  const ACTIVE = data?.workers || [];
  const TERMINATED = data?.terminated_workers || [];
  const groups = {};
  ACTIVE.forEach((w) => {
    const k = w.status || "Unmarked";
    (groups[k] = groups[k] || []).push(w);
  });
  const SECTION_ORDER = [
    ["Present", "✅", "Attended"],
    ["AL", "🟦", "Annual Leave"],
    ["Absent", "❌", "Absent"],
    ["Late", "⏰", "Late"],
    ["NG", "🌙", "Night Shift NG"],
    ["NG IN", "🟢", "NG IN — Confirmed"],
    ["MIA", "🔴", "MIA"],
    ["MC", "🏥", "MC"],
    ["Medical", "🏥", "Medical"],
    ["Off", "☕", "Off"],
    ["Unmarked", "⬜️", "Unmarked"],
  ];

  return (
    <div className="hidden print:block print-report" data-testid="jnj-print-report">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .print-report, .print-report * { visibility: visible !important; }
          .print-report {
            position: absolute; top: 0; left: 0; right: 0;
            color: #0b1220 !important; background: #fff !important;
            font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
            padding: 16px 22px;
          }
          .print-report .pr-divider { border-top: 1px solid #d9dbe1; margin: 8px 0; }
          .print-report h1 { font-size: 18px; margin: 0; }
          .print-report h2 { font-size: 13px; margin: 10px 0 4px; }
          .print-report table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .print-report td, .print-report th { padding: 3px 6px; vertical-align: top; }
          .print-report tr.term td { color: #6b7280; text-decoration: line-through; }
          .print-report .meta { font-size: 11px; color: #6b7280; }
          .print-report .note { font-style: italic; color: #4b5563; font-size: 10px; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
      <h1>{shift?.name} — Daily Attendance</h1>
      <div className="meta">{dateStr}</div>
      <div className="pr-divider" />
      <div className="meta" style={{ display: "flex", gap: 16 }}>
        <span><strong>Total:</strong> {ACTIVE.length}</span>
        <span><strong>Present:</strong> {(groups["Present"] || []).length}</span>
        <span><strong>Unmarked:</strong> {(groups["Unmarked"] || []).length}</span>
        <span><strong>Terminated:</strong> {TERMINATED.length}</span>
      </div>
      <div className="pr-divider" />

      {SECTION_ORDER.map(([key, emoji, label]) => {
        const rows = groups[key];
        if (!rows || rows.length === 0) return null;
        return (
          <div key={key}>
            <h2>{emoji} {label} ({rows.length})</h2>
            <table>
              <tbody>
                {rows.map((w, i) => (
                  <tr key={w.worker_id}>
                    <td style={{ width: 28, color: "#888" }}>{i + 1}.</td>
                    <td><strong>{w.name}</strong></td>
                    <td style={{ width: 90, textAlign: "right" }}>{w.check_in || ""}</td>
                    <td className="note" style={{ width: "40%" }}>{w.notes || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {TERMINATED.length > 0 && (
        <>
          <h2>⛔ Terminated / Resigned ({TERMINATED.length})</h2>
          <table>
            <tbody>
              {TERMINATED.map((t, i) => (
                <tr key={t.worker_id} className="term">
                  <td style={{ width: 28 }}>{i + 1}.</td>
                  <td>{t.name}</td>
                  <td style={{ width: 110, textAlign: "right" }}>
                    {t.kind === "resigned" ? "Resigned" : "Terminated"}
                  </td>
                  <td className="note">{t.termination_effective_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="pr-divider" />
      <div className="meta" style={{ display: "flex", justifyContent: "space-between" }}>
        <span>Generated {new Date().toLocaleString("en-GB")}</span>
        <span>Mercy Management System</span>
      </div>
    </div>
  );
}
